import * as Sentry from '@sentry/react';
import type { BrowserOptions } from '@sentry/react';
import type { ErrorInfo } from 'react';

import {
  asReportableError,
  describeRequest,
  keepIntegration,
  scrubBreadcrumb,
  scrubEvent,
  type ReportDetails,
} from 'src/errorReporting/shared';
import { log } from 'src/log';

/**
 * Error reporting in the renderers — the desktop's windows and the web build alike.
 *
 * Both run `@sentry/react`. The desktop starts it through `@sentry/electron/renderer`, which sends
 * through the main process (errorReporting.desktop.ts); the web build starts it directly
 * (src/web/errorReporting.ts). Everything here works the same after either, which is why the
 * rest of the app only ever imports this file.
 *
 * Uncaught errors are reported by the SDK on its own. What it cannot see is an error the app
 * catches and turns into a message — "something went wrong", "could not load" — so the places
 * that do that call `reportCaughtError`.
 */

/** The options both renderers share. See src/errorReporting/shared.ts for what is left out. */
export function rendererReportingOptions(environment: string, release: string): BrowserOptions {
  return {
    // Development builds report nothing: their errors belong in a console, not in a project.
    enabled: !__DEV__,
    environment,
    release,
    integrations: (defaults) => [
      ...defaults.filter((integration) => keepIntegration(integration) && integration.name !== 'Breadcrumbs'),
      // Requests and navigations, but not console output or clicks — see scrubBreadcrumb.
      Sentry.breadcrumbsIntegration({ console: false, dom: false }),
    ],
    sendDefaultPii: false,
    sendClientReports: false,
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  };
}

/**
 * Reports an error the app caught and showed as a generic message.
 *
 * `shown` is the translation key of what the person read, so an issue says what it looked like
 * from their side, and every "unknown error" can be found by that one key.
 *
 * Only ever sends an Error. The services here throw axios responses when the API refuses, and
 * the SDK serialises a thrown object whole — its request body, which can be a report, and its
 * `Authorization` header with it. So an object is described instead: the method, path and
 * status of the request, and the API's error id if it gave one.
 */
export function reportCaughtError(error: unknown, shown: string, details?: ReportDetails): void {
  Sentry.withScope((scope) => {
    scope.setTag('shown', shown);

    const request = describeRequest(error);
    if (request) scope.setContext('request', request);
    if (details) scope.setContext('details', details);

    Sentry.captureException(asReportableError(error));
  });
}

/** The account errors are filed under, or none. The id rather than the address. */
export function setReportingAccount(accountId: string | null): void {
  Sentry.setUser(accountId ? { id: accountId } : null);
}

const reportUncaught = Sentry.reactErrorHandler();

/**
 * For `createRoot`: what escaped every error boundary, and what a boundary caught and replaced
 * with a fallback — the editor's, for one. Both are still printed, as React would have.
 */
export const reactRootErrorOptions = {
  onUncaughtError: (error: unknown, errorInfo: ErrorInfo) => {
    reportUncaught(error, errorInfo);
    log.error('Uncaught error in the React tree', error);
  },
  onCaughtError: Sentry.reactErrorHandler((error: unknown) => {
    log.error('Error caught by a React error boundary', error);
  }),
};
